import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import axios from "axios";
import { BACKEND_URL } from "../data/config";

const SettingsPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  const [editPassword, setEditPassword] = useState({ id: null, password: "" });
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [modalMessage, setModalMessage] = useState({ show: false, message: "", type: "info" });

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/users-list.php`);
      setUsers(res.data.data || []);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load users.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const showMessage = (message, type = "info") => {
    setModalMessage({ show: true, message, type });
    setTimeout(() => setModalMessage({ show: false, message: "", type: "info" }), 3000);
  };

  const addUser = async () => {
    if (!newUser.username || !newUser.password) return showMessage("Fill all fields", "error");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/add-user.php`, newUser);
      showMessage(res.data.message, res.data.success ? "success" : "error");
      if (res.data.success) { setNewUser({ username: "", password: "" }); loadUsers(); }
    } catch (err) {
      console.error(err);
      showMessage("Failed to add user.", "error");
    }
  };

  const savePassword = async () => {
    if (!editPassword.password) return showMessage("Enter a new password", "error");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/update-password.php`, editPassword);
      showMessage(res.data.message, res.data.success ? "success" : "error");
      if (res.data.success) { setEditPassword({ id: null, password: "" }); loadUsers(); }
    } catch (err) {
      console.error(err);
      showMessage("Failed to update password.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteUserId) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/delete-user.php`, { id: deleteUserId });
      showMessage(res.data.message, res.data.success ? "success" : "error");
      if (res.data.success) { loadUsers(); }
      setDeleteUserId(null);
    } catch (err) {
      console.error(err);
      showMessage("Failed to delete user.", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="max-w-4xl mx-auto p-6 space-y-6">

        <h1 className="text-2xl font-bold text-gray-900">User Settings</h1>

        {/* Add User */}
        <div className="p-4 bg-white rounded shadow-md space-y-3">
          <h2 className="font-semibold text-gray-800">Add New User</h2>
          <div className="flex flex-col md:flex-row gap-2">
            <input type="text" placeholder="Username" value={newUser.username} 
                   onChange={e => setNewUser({...newUser, username: e.target.value})}
                   className="border px-3 py-2 rounded w-full md:w-1/3" />
            <input type="password" placeholder="Password" value={newUser.password} 
                   onChange={e => setNewUser({...newUser, password: e.target.value})}
                   className="border px-3 py-2 rounded w-full md:w-1/3" />
            <button onClick={addUser} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Add User</button>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white border rounded shadow overflow-x-auto">
          <table className="w-full text-sm table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Username</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="p-4 text-center">Loading...</td></tr>
              ) : users.length ? users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{user.id}</td>
                  <td className="px-4 py-2">{user.username}</td>
                  <td className="px-4 py-2 flex gap-2">
                    <button onClick={() => setEditPassword({ id: user.id, password: "" })}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700">Edit Password</button>
                    <button onClick={() => setDeleteUserId(user.id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={3} className="p-4 text-center text-gray-500">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Edit Password Modal */}
        {editPassword.id && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 !m-0">
            <div className="bg-white p-6 rounded shadow w-80">
              <h2 className="text-lg font-semibold mb-4">Edit Password</h2>
              <input type="password" placeholder="New Password" value={editPassword.password} 
                     onChange={e => setEditPassword({...editPassword, password: e.target.value})}
                     className="w-full border px-3 py-2 mb-4 rounded" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditPassword({ id: null, password: "" })}
                        className="px-3 py-1 border rounded hover:bg-gray-100">Cancel</button>
                <button onClick={savePassword} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 !m-0">
            <div className="bg-white p-6 rounded shadow w-80">
              <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
              <p className="mb-4">Are you sure you want to delete this user?</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeleteUserId(null)}
                        className="px-3 py-1 border rounded hover:bg-gray-100">Cancel</button>
                <button onClick={confirmDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Message Modal */}
        {modalMessage.show && (
          <div className="fixed bottom-6 right-6 bg-white p-4 rounded shadow-md border-l-4"
               style={{ borderColor: modalMessage.type === "error" ? "red" : "green" }}>
            {modalMessage.message}
          </div>
        )}

      </div>
    </div>
  );
};

export default SettingsPage;